/**
 * The tape at a glance (DOP-627): one strip above the table, in the style of
 * the KPI and monitoring summary rows.
 *
 * The canvas drew pool statistics here (balance, weighted rate, FICO). A run
 * carries its first twenty rows and nothing aggregated, and a weighted average
 * of twenty loans labelled as the pool's would be wrong, so the strip says what
 * the run does know: how many loans, how many columns, and how each column was
 * made.
 */

import React from "react";
import type { OriginCounts } from "./loanTapeColumns";

export interface TapeGlanceProps {
	loans: number | null;
	columns: number;
	counts: OriginCounts;
}

interface CellProps {
	label: string;
	value: string;
	attention?: boolean;
}

const Cell: React.FC<CellProps> = ({ label, value, attention = false }) => (
	<div className="border-r border-[#f1f2f4] px-4 py-3 last:border-r-0">
		<dt className="text-xs text-[#6B7280]">{label}</dt>
		<dd
			className={`mt-1 text-xl font-bold tabular-nums ${attention ? "text-[#b54708]" : "text-[#111111]"}`}
		>
			{value}
		</dd>
	</div>
);

const TapeGlance: React.FC<TapeGlanceProps> = ({ loans, columns, counts }) => (
	<dl
		aria-label="This tape at a glance"
		className="grid grid-cols-2 rounded-xl border border-gray-200 bg-white sm:grid-cols-5"
	>
		<Cell label="Loans" value={loans === null ? "—" : loans.toLocaleString()} />
		<Cell label="Columns" value={String(columns)} />
		<Cell label="Mapped" value={String(counts.mapped)} />
		<Cell label="Derived" value={String(counts.derived)} />
		<Cell
			label="Unmapped"
			value={String(counts.unmapped)}
			attention={counts.unmapped > 0}
		/>
	</dl>
);

export default TapeGlance;
